/** Per-strike OI-change trend columns: ONE canvas per side with up to six
 * colored lines, one per strike (the ladder rule — top-3 |ΔOI| +
 * ATM/ITM1/OTM1), showing WHICH strikes writers are building or
 * unwinding. Legend chips toggle a line's visibility (all shown by
 * default; OTM always stays visible). The /api/market/oi-drift data is
 * owned by OptionsTab so the ladder bars can share the strike colors. */

import { useState, type ReactElement } from "react";
import { cn } from "@/lib/cn";
import { formatSignedOi } from "@/lib/oiFormat";
import { boundsWithZero, xScale, yScale, type Bounds } from "@/components/Reports/analyticsMath";
import type { DriftMode, Moneyness, OiDriftResponse, OiStrikeSeries } from "@/types/market";
import {
  ChartTipBox,
  H,
  HoverCrosshair,
  MODE_LABELS,
  W,
  XAxisLabels,
  YAxisLabels,
  YGrid,
  ZeroDash,
  axisLabel,
  deltaTone,
  hoverIndex,
  niceTicks,
  svgPath,
  useChartHover,
  zoomSlice,
} from "./driftShared";

// Six visually distinct hues, index-matched to a side's series order
// (nearest-to-spot first). CALLS wear the DARK shade of each hue and PUTS
// the LIGHT one (operator request 2026-07-27), so the side is readable at
// a glance even when both charts show the same strike.
const CALL_LINE_COLORS = ["#1d4ed8", "#c2410c", "#7e22ce", "#0f766e", "#be185d", "#a16207"];
const PUT_LINE_COLORS = ["#60a5fa", "#fb923c", "#c084fc", "#2dd4bf", "#f472b6", "#facc15"];

export type OptionSide = "call" | "put";

/** Strike → line color, assigned by a side's series order. The ONE color
 * authority for the tab: the trend lines here and the OptionsPanel ladder
 * bars both read it, so a strike always wears the same color in both. */
export function strikeColorMap(
  series: readonly OiStrikeSeries[],
  side: OptionSide,
): ReadonlyMap<number, string> {
  const palette = side === "call" ? CALL_LINE_COLORS : PUT_LINE_COLORS;
  return new Map(series.map((s, i) => [s.strike, palette[i % palette.length] ?? "#1d4ed8"]));
}

export function OiChangeRow({
  data,
  mode,
  zoom = 1,
}: Readonly<{ data: OiDriftResponse | undefined; mode: DriftMode; zoom?: number }>): ReactElement {
  const call = sliceAll(data?.call ?? [], zoom);
  const put = sliceAll(data?.put ?? [], zoom);
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <OiStrikeChart label="Call OI Change" side="call" series={call} mode={mode} />
      <OiStrikeChart label="Put OI Change" side="put" series={put} mode={mode} />
    </div>
  );
}

/** The time zoom applied per strike series (latest window kept). */
function sliceAll(series: readonly OiStrikeSeries[], zoom: number): OiStrikeSeries[] {
  if (zoom <= 1) return [...series];
  return series.map((s) => ({ ...s, points: [...zoomSlice(s.points, zoom)] }));
}

/** One strike's series with its stable color (assigned by ladder order,
 * so hiding a line never recolors its neighbours). */
interface StrikeEntry {
  series: OiStrikeSeries;
  color: string;
}

/** OTM lines are always visible — every other line can be hidden by
 * clicking its legend chip (all shown by default). */
function canHide(series: OiStrikeSeries): boolean {
  return series.moneyness !== "OTM1";
}

function OiStrikeChart({
  label,
  side,
  series,
  mode,
}: Readonly<{
  label: string;
  side: OptionSide;
  series: readonly OiStrikeSeries[];
  mode: DriftMode;
}>): ReactElement {
  const [hidden, setHidden] = useState<ReadonlySet<number>>(new Set());
  const colors = strikeColorMap(series, side);
  const entries = series.map((s) => ({ series: s, color: colors.get(s.strike) ?? "#1d4ed8" }));
  const visible = entries.filter((e) => !canHide(e.series) || !hidden.has(e.series.strike));
  const toggle = (s: OiStrikeSeries): void => {
    if (!canHide(s)) return;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(s.strike)) next.delete(s.strike);
      else next.add(s.strike);
      return next;
    });
  };
  return (
    <div>
      <h4
        className="mb-1 text-xs text-ink-secondary"
        title="One line per strike (top-3 by |ΔOI| + ATM/ITM1/OTM1, up to 6). Intraday = change vs today's open; day-over-day = vs the previous day. Above the dashed zero line = OI building, below = unwinding. Click a legend chip to hide/show its line (OTM always stays visible)."
      >
        {label}
      </h4>
      {series.length === 0 ? (
        <div className="text-xs text-ink-muted">Needs two or more OI points.</div>
      ) : (
        <>
          <OiStrikeSvg label={label} entries={visible} mode={mode} />
          <XAxisLabels series={series[0]?.points ?? []} mode={mode} />
          <StrikeLegend label={label} entries={entries} hidden={hidden} onToggle={toggle} />
        </>
      )}
    </div>
  );
}

function OiStrikeSvg({
  label,
  entries,
  mode,
}: Readonly<{ label: string; entries: readonly StrikeEntry[]; mode: DriftMode }>): ReactElement {
  // ONE zero-spanning bounds across every VISIBLE strike's series, so the
  // lines are directly comparable within the canvas.
  const bounds = boundsWithZero(entries.flatMap((e) => e.series.points.map((p) => p.change)));
  const ticks = niceTicks(bounds);
  const hover = useChartHover();
  // The longest visible series carries the time axis for hover snapping.
  const axis = entries.reduce<readonly { at: string; change: number }[]>(
    (best, e) => (e.series.points.length > best.length ? e.series.points : best),
    [],
  );
  const hovered = hoverIndex(hover.frac, axis.length);
  const at = hovered === null ? undefined : axis[hovered]?.at;
  // The tooltip is SPECIFIC to the line nearest the cursor (operator
  // request 2026-07-27) — hovering the 24,500 line talks about 24,500.
  const pick = pickNearestLine(entries, at, hover.fracY, bounds);
  return (
    <div
      ref={hover.ref}
      className="relative"
      data-testid={`oi-hover-${label}`}
      onMouseMove={hover.onMove}
      onMouseLeave={hover.onLeave}
    >
      <svg
        viewBox={`0 0 ${String(W)} ${String(H)}`}
        preserveAspectRatio="none"
        className="h-24 w-full"
        role="img"
        aria-label={`${label} (${MODE_LABELS[mode].toLowerCase()})`}
      >
        <YGrid values={ticks} bounds={bounds} height={H} />
        <ZeroDash y={yScale(0, bounds, H)} />
        {entries.map((e) => (
          <StrikeLine key={e.series.strike} series={e.series} color={e.color} bounds={bounds} />
        ))}
        {hovered !== null && <HoverCrosshair i={hovered} n={axis.length} />}
        {hovered !== null && pick !== null && (
          <PickDot i={hovered} n={axis.length} pick={pick} bounds={bounds} />
        )}
      </svg>
      {/* OI values live on the RIGHT edge (Sensibull convention) — the
          left stays clear for the index-level charts' NIFTY ticks. */}
      <YAxisLabels values={ticks} bounds={bounds} height={H} format={formatOiTick} side="right" />
      {pick !== null && at !== undefined && hover.frac !== null && (
        <OiTip pick={pick} at={at} mode={mode} frac={hover.frac} />
      )}
    </div>
  );
}

interface LinePick {
  entry: StrikeEntry;
  change: number;
}

/** Marker dot on the picked line at the hovered snapshot. */
function PickDot({
  i,
  n,
  pick,
  bounds,
}: Readonly<{ i: number; n: number; pick: LinePick; bounds: Bounds }>): ReactElement {
  return (
    <circle
      cx={xScale(i, n, W)}
      cy={yScale(pick.change, bounds, H)}
      r={3.5}
      fill={pick.entry.color}
    />
  );
}

/** The visible strike whose line sits nearest the cursor's vertical
 * position at the hovered snapshot; strikes with no point there can't
 * be located and are skipped. */
function pickNearestLine(
  entries: readonly StrikeEntry[],
  at: string | undefined,
  fracY: number | null,
  bounds: Bounds,
): LinePick | null {
  if (at === undefined || fracY === null) return null;
  let best: (LinePick & { d: number }) | null = null;
  for (const entry of entries) {
    const p = entry.series.points.find((pt) => pt.at === at);
    if (p === undefined) continue;
    const d = Math.abs(yScale(p.change, bounds, H) / H - fracY);
    if (best === null || d < best.d) best = { entry, change: p.change, d };
  }
  return best === null ? null : { entry: best.entry, change: best.change };
}

/** Hover tooltip for ONE strike: snapshot time, the strike (with its
 * line color and moneyness), and its OI change at that instant. */
function OiTip({
  pick,
  at,
  mode,
  frac,
}: Readonly<{ pick: LinePick; at: string; mode: DriftMode; frac: number }>): ReactElement {
  const s = pick.entry.series;
  return (
    <ChartTipBox frac={frac}>
      <div className="mb-1 text-ink-muted">{axisLabel(at, mode)}</div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1">
          <span
            className="inline-block h-0.5 w-3 rounded-full"
            style={{ backgroundColor: pick.entry.color }}
          />
          <span className="font-mono text-ink">{s.strike.toLocaleString("en-IN")}</span>
          {s.moneyness !== null && <span className="text-ink-muted">{s.moneyness}</span>}
        </span>
        <span className={cn("font-mono", deltaTone(pick.change))}>
          {formatSignedOi(pick.change)}
        </span>
      </div>
    </ChartTipBox>
  );
}

/** Signed lakh/crore tick labels; plain "0" at the baseline (a "+0"
 * tick reads like data). */
function formatOiTick(v: number): string {
  return v === 0 ? "0" : formatSignedOi(v);
}

function StrikeLine({
  series,
  color,
  bounds,
}: Readonly<{ series: OiStrikeSeries; color: string; bounds: Bounds }>): ReactElement {
  if (series.points.length === 1) {
    const only = series.points[0];
    return <circle cx={4} cy={yScale(only?.change ?? 0, bounds, H)} r={3} fill={color} />;
  }
  return (
    <path
      d={svgPath(
        series.points.map((p) => p.change),
        bounds,
      )}
      fill="none"
      stroke={color}
      strokeWidth={1.75}
      strokeLinejoin="round"
    />
  );
}

function StrikeLegend({
  label,
  entries,
  hidden,
  onToggle,
}: Readonly<{
  label: string;
  entries: readonly StrikeEntry[];
  hidden: ReadonlySet<number>;
  onToggle: (series: OiStrikeSeries) => void;
}>): ReactElement {
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
      {entries.map((e) => (
        <LegendChip
          key={e.series.strike}
          label={label}
          entry={e}
          isHidden={canHide(e.series) && hidden.has(e.series.strike)}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

function LegendChip({
  label,
  entry,
  isHidden,
  onToggle,
}: Readonly<{
  label: string;
  entry: StrikeEntry;
  isHidden: boolean;
  onToggle: (series: OiStrikeSeries) => void;
}>): ReactElement {
  const s = entry.series;
  return (
    <button
      type="button"
      aria-label={`${label}: toggle ${s.strike.toLocaleString("en-IN")}`}
      title={canHide(s) ? "Click to hide/show this line" : "OTM line always stays visible"}
      className={cn(
        "flex items-center gap-1 text-ink-secondary",
        canHide(s) && "cursor-pointer",
        isHidden && "opacity-40",
      )}
      onClick={() => {
        onToggle(s);
      }}
    >
      <span
        className="inline-block h-0.5 w-3 rounded-full"
        style={{ backgroundColor: entry.color }}
      />
      <span className={cn("font-mono", isHidden && "line-through")}>
        {s.strike.toLocaleString("en-IN")}
      </span>
      <MoneynessBadge moneyness={s.moneyness} />
      <span className={cn("font-mono", deltaTone(s.points.at(-1)?.change ?? 0))}>
        {formatSignedOi(s.points.at(-1)?.change ?? 0)}
      </span>
    </button>
  );
}

function MoneynessBadge({
  moneyness,
}: Readonly<{ moneyness: Moneyness | null }>): ReactElement | null {
  if (moneyness === null) return null;
  return (
    <span
      className={cn(
        "rounded px-1 text-[10px] font-medium leading-4",
        moneyness === "ATM" ? "bg-accent-bg text-accent" : "bg-surface-2 text-ink-secondary",
      )}
    >
      {moneyness}
    </span>
  );
}
